import React from 'react';
import { APP_TEXT } from '@/constant/theme/colors.constant';

// ─── Types ──────────────────────────────────────────────────────────────────

type TextAs =
  | 'p'
  | 'span'
  | 'label'
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'small'
  | 'strong'
  | 'em'
  | 'code';

type TextSize =
  | '2xs' // 8px
  | 'xs' // 9px
  | 'sm' // 10px
  | 'md' // 11px  ← most common in codebase
  | 'base' // 12px
  | 'lg' // 13px
  | 'xl' // 14px
  | '2xl' // 16px
  | '3xl' // 18px
  | '4xl' // 20px
  | '5xl' // 24px
  | '6xl' // 30px (text-3xl)
  | '7xl' // 36px (text-4xl)
  | '8xl' // 48px (text-5xl)
  | 'display'; // 2rem

type TextWeight =
  | 'light'
  | 'normal'
  | 'medium'
  | 'semibold'
  | 'bold'
  | 'extrabold'
  | 'black';

type TextColor =
  | 'default' // gray-900 / white
  | 'soft' // gray-700 / gray-300  ← form labels, secondary headings
  | 'muted' // gray-500 / gray-400
  | 'subtle' // gray-400 / gray-500
  | 'faint' // gray-300 / gray-600
  | 'primary' // brand accent
  | 'secondary' // brand secondary
  | 'white' // always white
  | 'white-soft' // white/70
  | 'white-muted' // white/50
  | 'white-dim' // white/40
  | 'danger' // red
  | 'success' // green/emerald
  | 'warning' // amber/yellow
  | 'info' // blue
  | 'indigo'
  | 'inherit';

type TextTracking =
  | 'tighter'
  | 'tight'
  | 'normal'
  | 'wide'
  | 'wider'
  | 'widest'
  | 'superwide';
type TextLeading = 'none' | 'tight' | 'snug' | 'normal' | 'relaxed' | 'loose';
type TextAlign = 'left' | 'center' | 'right' | 'justify';

export interface TextProps {
  /** HTML element to render. Defaults to `"p"` */
  as?: TextAs;
  /** Size token (maps to pixel values common in the codebase) */
  size?: TextSize;
  /** Font weight */
  weight?: TextWeight;
  /** Color token with automatic dark-mode pair */
  color?: TextColor;
  /** Letter spacing */
  tracking?: TextTracking;
  /** Line height */
  leading?: TextLeading;
  /** Text alignment */
  align?: TextAlign;
  /** UPPERCASE transform */
  uppercase?: boolean;
  /** Truncate with ellipsis (single line) */
  truncate?: boolean;
  /** Monospace font */
  mono?: boolean;
  /** Italic */
  italic?: boolean;
  /** Line clamp (1–6) */
  clamp?: 1 | 2 | 3 | 4 | 5 | 6;
  /** Extra Tailwind classes */
  className?: string;
  /** Inline styles (e.g. custom color) */
  style?: React.CSSProperties;
  children?: React.ReactNode;
  /** aria-label */
  'aria-label'?: string;
  htmlFor?: string;
  id?: string;
  onClick?: (e: React.MouseEvent) => void;
  title?: string;
}

// ─── Maps ───────────────────────────────────────────────────────────────────

const SIZE_MAP: Record<TextSize, string> = {
  '2xs': 'text-[8px]',
  xs: 'text-[9px]',
  sm: 'text-[10px]',
  md: 'text-[11px]',
  base: 'text-[12px]',
  lg: 'text-[13px]',
  xl: 'text-[14px]',
  '2xl': 'text-base', // 16px
  '3xl': 'text-[18px]',
  '4xl': 'text-xl', // 20px
  '5xl': 'text-2xl', // 24px
  '6xl': 'text-3xl', // 30px
  '7xl': 'text-4xl', // 36px
  '8xl': 'text-5xl', // 48px
  display: 'text-[2rem]',
};

const WEIGHT_MAP: Record<TextWeight, string> = {
  light: 'font-light',
  normal: 'font-normal',
  medium: 'font-medium',
  semibold: 'font-semibold',
  bold: 'font-bold',
  extrabold: 'font-extrabold',
  black: 'font-[900]',
};

const COLOR_MAP: Record<TextColor, string> = {
  default: APP_TEXT.default,
  soft: APP_TEXT.soft,
  muted: APP_TEXT.muted,
  subtle: APP_TEXT.subtle,
  faint: APP_TEXT.faint,
  primary: APP_TEXT.primary,
  secondary: APP_TEXT.secondary,
  white: APP_TEXT.white,
  'white-soft': APP_TEXT['white-soft'],
  'white-muted': APP_TEXT['white-muted'],
  'white-dim': APP_TEXT['white-dim'],
  danger: APP_TEXT.danger,
  success: APP_TEXT.success,
  warning: APP_TEXT.warning,
  info: APP_TEXT.info,
  indigo: APP_TEXT.indigo,
  inherit: APP_TEXT.inherit,
};

const TRACKING_MAP: Record<TextTracking, string> = {
  tighter: 'tracking-tighter',
  tight: 'tracking-tight',
  normal: 'tracking-normal',
  wide: 'tracking-wide',
  wider: 'tracking-wider',
  widest: 'tracking-widest',
  superwide: 'tracking-[0.25em]',
};

const LEADING_MAP: Record<TextLeading, string> = {
  none: 'leading-none',
  tight: 'leading-tight',
  snug: 'leading-snug',
  normal: 'leading-normal',
  relaxed: 'leading-relaxed',
  loose: 'leading-loose',
};

const ALIGN_MAP: Record<TextAlign, string> = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
  justify: 'text-justify',
};

const CLAMP_MAP: Record<number, string> = {
  1: 'line-clamp-1',
  2: 'line-clamp-2',
  3: 'line-clamp-3',
  4: 'line-clamp-4',
  5: 'line-clamp-5',
  6: 'line-clamp-6',
};

// ─── Component ──────────────────────────────────────────────────────────────

/**
 * `Text` — universal typography atom.
 *
 * Covers every text pattern used across the app:
 * micro-labels, body copy, headings, gradient titles, mono code, truncated cells.
 *
 * @example
 * // Section label
 * <Text size="sm" weight="bold" color="muted" uppercase tracking="wider">Provider ID</Text>
 *
 * // Gradient heading
 * <Text as="h1" size="3xl" weight="extrabold" gradient tracking="tight">Dashboard</Text>
 *
 * // Muted truncated cell
 * <Text size="md" color="muted" truncate>{longValue}</Text>
 *
 * // Monospace code
 * <Text size="base" mono color="primary">ABC-12345</Text>
 */
export const Text = ({
  as: Tag = 'p',
  size = '2xl',
  weight = 'normal',
  color = 'default',
  tracking,
  leading,
  align,
  uppercase = false,
  truncate = false,
  mono = false,
  italic = false,
  clamp,
  className = '',
  style,
  children,
  htmlFor,
  ...rest
}: TextProps) => {
  const classes = [
    SIZE_MAP[size],
    WEIGHT_MAP[weight],
    COLOR_MAP[color],
    tracking ? TRACKING_MAP[tracking] : '',
    leading ? LEADING_MAP[leading] : '',
    align ? ALIGN_MAP[align] : '',
    uppercase ? 'uppercase' : '',
    truncate ? 'truncate' : '',
    mono ? 'font-mono' : '',
    italic ? 'italic' : '',
    clamp ? CLAMP_MAP[clamp] : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <Tag className={classes} style={style} htmlFor={htmlFor} {...rest}>
      {children}
    </Tag>
  );
};

// ─── Convenience variants ────────────────────────────────────────────────────

/**
 * Page / section hero heading — extrabold, h1
 * Replaces: <h1 className="text-2xl font-extrabold text-gray-900 dark:text-white tracking-tight">
 */
export const Heading = (
  props: Omit<TextProps, 'as' | 'weight' | 'size'> & { size?: TextSize },
) => (
  <Text
    as="h1"
    size="3xl"
    weight="extrabold"
    color="default"
    tracking="tight"
    {...props}
  />
);

/**
 * Section sub-heading — bold, default color, h2/h3
 * Replaces: <h2 className="text-xl font-bold text-gray-900 dark:text-white">
 */
export const SubHeading = (props: Omit<TextProps, 'as'>) => (
  <Text as="h2" size="xl" weight="bold" color="default" {...props} />
);

/**
 * Standard body paragraph — 16px normal
 * Replaces: <p className="text-base text-gray-900 dark:text-white">
 */
export const Body = (props: Omit<TextProps, 'as'>) => (
  <Text as="p" size="2xl" weight="normal" color="default" {...props} />
);

/**
 * Form / card field label — 14px bold, default color
 * Most common: <label className="text-sm font-bold text-gray-700 dark:text-gray-300">
 * Usage: <FieldLabel>Provider Name</FieldLabel>
 */
export const FieldLabel = (props: Omit<TextProps, 'as'>) => (
  <Text
    as="span"
    size="base"
    weight="bold"
    color="soft"
    leading="none"
    {...props}
  />
);

/**
 * Section micro-label — 11px semibold, uppercase, wide tracking, muted
 * Most common: <span className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
 * Usage: <Label>Section Title</Label>
 */
export const Label = (
  props: Omit<TextProps, 'as' | 'uppercase' | 'tracking' | 'weight'>,
) => (
  <Text
    as="span"
    size="md"
    weight="semibold"
    color="muted"
    uppercase
    tracking="wide"
    {...props}
  />
);

/**
 * Compact micro-label — 9px bold, uppercase, widest tracking, muted
 * Common: <span className="text-[9px] font-bold uppercase tracking-widest text-gray-400 dark:text-gray-500">
 * Usage: sidebar section headers, dense table sub-labels
 */
export const MicroLabel = (
  props: Omit<TextProps, 'as' | 'uppercase' | 'tracking' | 'weight'>,
) => (
  <Text
    as="span"
    size="xs"
    weight="bold"
    color="subtle"
    uppercase
    tracking="widest"
    {...props}
  />
);

/**
 * Caption / helper text — 11px normal, subtle
 * Common: <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">
 * Usage: form hints, status subtitles
 */
export const Caption = (props: Omit<TextProps, 'as'>) => (
  <Text as="span" size="xs" weight="normal" color="subtle" {...props} />
);

/**
 * Table / list cell value — 11px medium, default color, truncated
 * Most common: <span className="text-[11px] font-medium text-gray-900 dark:text-white truncate">
 */
export const CellValue = (props: Omit<TextProps, 'as' | 'truncate'>) => (
  <Text
    as="span"
    size="md"
    weight="medium"
    color="default"
    truncate
    {...props}
  />
);

/**
 * Muted secondary cell — 11px normal, muted color
 * Common: <span className="text-[11px] text-gray-500 dark:text-gray-400">
 */
export const CellMuted = (props: Omit<TextProps, 'as'>) => (
  <Text as="span" size="md" weight="normal" color="muted" {...props} />
);

/**
 * Monospace value — for IDs, codes, timestamps
 * Replaces: <span className="text-xs font-mono text-primary">
 */
export const Mono = (props: Omit<TextProps, 'as' | 'mono'>) => (
  <Text as="code" size="base" mono color="primary" {...props} />
);

export default Text;
